import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Knowledge Base - Security Guide
 */
const SecurityGuidePage = () => {
  const securityFeatures = [
    {
      title: "Data Encryption",
      description: "All data is encrypted both in transit and at rest.",
      details: [
        "AES-256 encryption for data at rest",
        "TLS 1.3 for all data in transit",
        "End-to-end encryption for sensitive PHI",
        "Encryption key management with HSM"
      ]
    },
    {
      title: "Access Control",
      description: "Granular role-based access control ensures users only see what they need.",
      details: [
        "Role-based permissions (Admin, Manager, User, Viewer)",
        "Department and facility-level access restrictions",
        "Data-level security for sensitive information",
        "Session timeout and automatic logout"
      ]
    },
    {
      title: "Authentication",
      description: "Secure authentication with multiple options.",
      details: [
        "Multi-factor authentication (MFA)",
        "Single Sign-On (SSO) with SAML 2.0",
        "Strong password requirements",
        "Account lockout after failed attempts"
      ]
    },
    {
      title: "Audit Logging",
      description: "Comprehensive audit trails for compliance and security monitoring.",
      details: [
        "All user actions logged with timestamps",
        "PHI access logging per HIPAA requirements",
        "Immutable audit records",
        "Audit log export for compliance reviews"
      ]
    }
  ];

  const complianceCertifications = [
    {
      title: "HIPAA",
      description: "Full compliance with HIPAA Privacy and Security Rules",
      icon: "🏥"
    },
    {
      title: "SOC 2 Type II",
      description: "Annual third-party security audits",
      icon: "✓"
    },
    {
      title: "HITRUST",
      description: "HITRUST CSF certified",
      icon: "🛡️"
    }
  ];

  const securityBestPractices = [
    {
      title: "Enable MFA",
      description: "Require multi-factor authentication for all users, especially administrators."
    },
    {
      title: "Review Access Regularly",
      description: "Conduct quarterly access reviews to ensure appropriate permissions."
    },
    {
      title: "Train Your Team",
      description: "Ensure all users complete security awareness training."
    },
    {
      title: "Monitor Audit Logs",
      description: "Review audit logs regularly for unusual activity."
    },
    {
      title: "Use Strong Passwords",
      description: "Enforce strong password policies and regular rotation."
    },
    {
      title: "Secure Workstations",
      description: "Ensure devices accessing Videxa meet security requirements."
    }
  ];

  return (
    <div className="security-guide-page">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#0E3056] to-[#1a4a7a] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/knowledge-base" className="text-blue-300 hover:text-white mb-4 inline-block">
              ← Back to Knowledge Base
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Security Guide</h1>
            <p className="text-xl text-gray-200">
              Learn about Videxa's security features and best practices for protecting your data.
            </p>
          </div>
        </div>
      </section>

      {/* Compliance Certifications */}
      <section className="py-12 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              {complianceCertifications.map((cert, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md text-center"
                >
                  <div className="text-4xl mb-3">{cert.icon}</div>
                  <h3 className="text-xl font-bold text-[#0E3056] dark:text-[#FFC000] mb-2">
                    {cert.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {cert.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
              Security Features
            </h2>

            <div className="space-y-6">
              {securityFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6"
                >
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    {feature.description}
                  </p>
                  <ul className="grid md:grid-cols-2 gap-2">
                    {feature.details.map((detail, idx) => (
                      <li key={idx} className="flex items-center text-gray-700 dark:text-gray-300">
                        <span className="text-green-500 mr-2">✓</span>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
              Security Best Practices
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {securityBestPractices.map((practice, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md"
                >
                  <h3 className="text-lg font-semibold text-[#0E3056] dark:text-[#FFC000] mb-2">
                    {practice.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {practice.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Security */}
      <section className="py-16 bg-[#0E3056] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Security Questions?</h2>
          <p className="text-gray-200 mb-8 max-w-2xl mx-auto">
            Contact our security team for questions about our security practices or to report concerns.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-[#FFC000] text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
          >
            Contact Security Team
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SecurityGuidePage;
